import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Search, ShieldCheck, Truck, MapPin, UserPlus } from "lucide-react";

const HERO_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_avian-connect-3/artifacts/mg5dbkng_ovolaay%20poulet-mali.png";

export const Hero = () => {
  const brand = useBrand();
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const search = (e) => {
    e.preventDefault();
    navigate(`/catalogue${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  };

  return (
    <section className="relative overflow-hidden">
      <img src={HERO_IMG} alt="Marché avicole africain" loading="eager"
        className="absolute inset-0 h-full w-full object-cover object-top sm:object-center pointer-events-none" />
      <div className="absolute inset-0 hero-fade pointer-events-none" />

      <div className="relative container-page py-8 sm:py-28 lg:py-36 w-full">
        <div className="max-w-2xl animate-fade-up text-center sm:text-left mx-auto sm:mx-0">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent/90 text-accent-foreground px-4 py-1.5 text-xs font-semibold">
            <MapPin className="h-3.5 w-3.5" /> {t("hero.badge")}
          </span>
          <h1 className="mt-4 sm:mt-5 font-heading font-extrabold text-3xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
            {brand.tagline}
          </h1>
          <p className="mt-3 sm:mt-5 text-sm sm:text-lg text-white/85 max-w-xl leading-relaxed mx-auto sm:mx-0">{brand.description}</p>

          {!user && (
            <div className="mt-5 sm:mt-7 flex flex-wrap items-center justify-center sm:justify-start gap-4" data-testid="hero-cta">
              <Button onClick={() => navigate("/inscription")} data-testid="hero-signup-btn"
                className="h-14 px-9 rounded-full text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 shadow-2xl animate-bounce-slow">
                <UserPlus className="h-6 w-6 mr-2.5" /> {t("hero.ctaSignup")}
              </Button>
              <Link to="/connexion" data-testid="hero-login-link"
                className="text-sm font-semibold text-white/90 hover:text-white underline underline-offset-4">
                {t("hero.ctaLogin")}
              </Link>
            </div>
          )}

          <form onSubmit={search}
            className="mt-8 hidden sm:flex sm:flex-row gap-3 bg-card p-2.5 rounded-2xl sm:rounded-full shadow-xl max-w-xl mx-auto sm:mx-0">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
              <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={t("hero.searchPlaceholder")}
                className="w-full h-12 pl-12 pr-4 rounded-full bg-transparent outline-none text-base" />
            </div>
            <Button data-testid="hero-search-btn" type="submit" className="h-12 px-8 rounded-full text-base bg-primary hover:bg-primary/90">
              {t("common.search")}
            </Button>
          </form>

          <div className="mt-8 hidden sm:flex sm:flex-wrap justify-center sm:justify-start gap-x-6 gap-y-2 text-xs sm:text-sm text-white/90">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /> {t("hero.trust1")}</span>
            <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-accent" /> {t("hero.trust2")}</span>
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-accent" /> {t("hero.trust3")}</span>
          </div>
        </div>
      </div>
    </section>
  );
};
